import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { useNavigate, useLocation } from 'react-router-dom';

const ForceNameUpdate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, userProfile, loading } = useAuth();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // Do not block rendering if auth is still loading or if there's no logged in user
  if (loading || !currentUser) return <>{children}</>;

  // Check if profile is loaded and the name is missing
  const needsName = userProfile && (!userProfile.displayName || userProfile.displayName.trim() === '');

  if (needsName) {
    const handleSave = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!name.trim()) {
        setError('Please enter your full name.');
        return;
      }
      try {
        setSaving(true);
        setError('');
        // Update user profile in Firestore
        await updateDoc(doc(db, 'users', currentUser.uid), {
          displayName: name.trim()
        });
        
        // After successfully updating, redirect to the hub if they aren't already there
        if (location.pathname !== '/hub') {
          navigate('/hub', { replace: true });
        }
      } catch (err) {
        console.error(err);
        setError('Failed to update profile. Please try again.');
        setSaving(false);
      }
    };

    return (
      <div className="fixed inset-0 z-[9999] bg-md-surface/90 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-md-surface-container rounded-3xl p-8 max-w-md w-full shadow-2xl animate-fade-in text-center">
          <div className="w-16 h-16 bg-md-primary/10 text-md-primary rounded-full flex items-center justify-center mx-auto mb-6 text-3xl">
            👋
          </div>
          <h2 className="text-2xl font-black text-md-on-surface mb-2 tracking-tight">Welcome to the new Hub!</h2>
          <p className="text-md-on-surface-variant text-sm mb-6 leading-relaxed">
            We've upgraded our systems. To help you connect with others, please enter your full name before continuing.
          </p>
          
          <form onSubmit={handleSave} className="space-y-4">
            <div className="text-left">
              <label className="block text-xs font-bold text-md-on-surface-variant mb-1 uppercase tracking-wider">Full Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="E.g., John Doe"
                className="w-full bg-md-surface text-md-on-surface px-4 py-3 rounded-xl border border-md-outline-variant focus:border-md-primary focus:ring-1 focus:ring-md-primary outline-none transition-all"
                autoFocus
                disabled={saving}
              />
            </div>
            {error && <p className="text-md-error text-xs font-bold text-left">{error}</p>}
            <button 
              type="submit" 
              disabled={saving}
              className="w-full bg-md-primary text-md-on-primary font-bold py-3.5 rounded-full hover:bg-md-primary/90 transition-all disabled:opacity-50 active:scale-[0.98]"
            >
              {saving ? 'Saving...' : 'Save & Continue'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Render normal content if name is present
  return <>{children}</>;
};

export default ForceNameUpdate;
